import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, getRemoteSession, setRemoteSession } from '../api';
import { useAuthStore } from '../auth';

export function AppShell({ children }: { children: ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();

  async function logout() {
    if (refreshToken) {
      try {
        await api.logout(refreshToken);
      } catch {
        // ignore
      }
    }
    clear();
    navigate('/login');
  }

  // Remote pairing session (no-login device): minimal chrome, no admin nav.
  const remote = getRemoteSession();
  if (remote) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-10 border-b border-border bg-bg/85 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
            <div>
              <span className="text-lg font-extrabold tracking-tight">
                Score<span className="text-brand">Lync</span>
              </span>
              <span className="ml-3 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                Remote {remote.role === 'SCORER' ? 'scorer' : 'stats'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right text-xs text-muted">
                {remote.game.homeTeam} vs {remote.game.awayTeam}
              </div>
              <button
                onClick={() => {
                  setRemoteSession(null);
                  navigate('/join', { replace: true });
                }}
                className="btn btn-ghost text-sm"
              >
                Disconnect
              </button>
            </div>
          </div>
        </header>
        <main className="w-full flex-1 px-2 py-3 sm:px-3">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b border-border bg-bg/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-xl font-extrabold tracking-tight">
            Score<span className="text-brand">Lync</span>
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            {me?.user.isPlatformAdmin && (
              <NavLink to="/superadmin" current={location.pathname}>
                Super-admin
              </NavLink>
            )}
            {/* Stats keepers get a stripped nav — just Games. */}
            {me?.org.status === 'ACTIVE' &&
              me.user.role === 'STATS_KEEPER' && (
                <NavLink to="/games" current={location.pathname}>
                  Games
                </NavLink>
              )}
            {me?.org.status === 'ACTIVE' &&
              me.user.role !== 'STATS_KEEPER' && (
                <>
                  <NavLink to="/" current={location.pathname}>
                    Dashboard
                  </NavLink>
                  <NavLink to="/venues" current={location.pathname}>
                    Venues
                  </NavLink>
                  <NavLink to="/teams" current={location.pathname}>
                    Teams
                  </NavLink>
                  <NavLink to="/games" current={location.pathname}>
                    Games
                  </NavLink>
                  <NavLink to="/displays" current={location.pathname}>
                    Displays
                  </NavLink>
                  <NavLink to="/layouts" current={location.pathname}>
                    Layouts
                  </NavLink>
                  <NavLink to="/tournaments" current={location.pathname}>
                    Tournaments
                  </NavLink>
                  <NavLink to="/campaigns" current={location.pathname}>
                    Campaigns
                  </NavLink>
                  <NavLink to="/settings/members" current={location.pathname}>
                    Members
                  </NavLink>
                </>
              )}
            <div className="ml-2 flex items-center gap-3 border-l border-border pl-4">
              <div className="text-right">
                <div className="text-text">{me?.user.name}</div>
                <div className="text-xs text-muted">{me?.org.name}</div>
              </div>
              <button onClick={logout} className="btn btn-ghost text-sm">
                Log out
              </button>
            </div>
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-5 sm:px-5">
        {children}
      </main>
    </div>
  );
}

function NavLink({
  to,
  current,
  children,
}: {
  to: string;
  current: string;
  children: ReactNode;
}) {
  const active = current === to || current.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={`rounded-md px-3 py-1.5 transition ${
        active
          ? 'bg-brand/15 text-brand-light'
          : 'text-muted hover:text-text'
      }`}
    >
      {children}
    </Link>
  );
}
